import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Copy, ArrowLeft, Cake, Gift, Pencil, Crown, DollarSign, ShoppingBag, MapPin, Trash2, Download, Sparkles, Upload } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import LocationsSection from "@/components/LocationsSection";

const STEPS = [
  { key: "contract_sent", label: "Contract sent" },
  { key: "kickoff_done", label: "Kickoff completed" },
  { key: "first_order", label: "First order placed" },
  { key: "trained", label: "Reseller trained" },
];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [client, setClient] = useState(null);
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enhancingPhoto, setEnhancingPhoto] = useState(false);

  const load = async () => {
    const [c, pp, allOrders] = await Promise.all([
      api.get(`/clients/${id}`).then(r => r.data),
      api.get("/endpoint-partners").then(r => r.data).catch(() => []),
      api.get("/orders").then(r => r.data).catch(() => []),
    ]);
    setClient(c);
    setPartners(pp);
    setOrders(allOrders.filter(o => o.client_id === id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!client) return <div className="p-10 text-gray-400">Loading…</div>;

  const isVip = (client.lifetime_spend || 0) >= 5000;

  const toggleStep = async (key, value) => {
    await api.patch(`/clients/${id}/onboarding`, { [key]: value });
    load();
  };

  const portalUrl = `${window.location.origin}/shop/${client.slug}`;
  const copyUrl = async () => { await navigator.clipboard.writeText(portalUrl); toast.success("Resale URL copied"); };
  const downloadQr = () => {
    const svg = document.getElementById("client-substore-qr");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 900;
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${client.slug || "client"}-substore-qr.png`;
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  const openEdit = () => {
    setForm({
      name: client.name || "", company: client.company || "", email: client.email || "",
      phone: client.phone || "", address: client.address || "", zip_code: client.zip_code || "",
      city: client.city || "", state: client.state || "",
      lat: client.lat ?? "", lng: client.lng ?? "",
      photo_url: client.photo_url || "", notes: client.notes || "",
      balance: client.balance ?? 0, status: client.status || "New",
      birthday: client.birthday || "", anniversary: client.anniversary || "",
      endpoint_partner_id: client.endpoint_partner_id || "warehouse",
      has_multiple_locations: !!client.has_multiple_locations,
      additional_locations: client.additional_locations || [],
      wholesale_split_percent: client.wholesale_split_percent ?? null,
      license_number: client.license_number || "",
      license_image_url: client.license_image_url || "",
      license_status: client.license_status || "missing", position_title: client.position_title || "", other_position_title: "",
    });
    setEditOpen(true);
  };
  const enhancePhoto = async () => {
    setEnhancingPhoto(true);
    try {
      const { data } = await api.post(`/clients/${id}/enhance-photo`);
      if (data.photo_url) setClient((current) => ({ ...current, photo_url: data.photo_url }));
      toast.success(data.enhanced ? "Client photo enhanced naturally" : "Original photo retained");
    } catch (err) { toast.error(err.response?.data?.detail || "Photo enhancement failed"); }
    finally { setEnhancingPhoto(false); }
  };
  const replacePhoto = async (file) => { if (!file) return; const fd = new FormData(); fd.append("file", file); try { const { data } = await api.post(`/clients/${id}/photo-upload`, fd); setClient((current) => ({ ...current, photo_url: data.photo_url })); toast.success("Client photo replaced"); } catch (err) { toast.error(err.response?.data?.detail || "Photo replacement failed"); } };
  const enhanceLicense = async () => { try { const { data } = await api.post(`/clients/${id}/enhance-license`); if (data.license_image_url) setClient((current) => ({ ...current, license_image_url: data.license_image_url })); toast.success(data.enhanced ? "License enhanced naturally" : "Original license retained"); } catch (err) { toast.error(err.response?.data?.detail || "License enhancement failed"); } };
  const replaceLicense = async (file) => { if (!file) return; const fd = new FormData(); fd.append("file", file); try { const { data } = await api.post(`/clients/${id}/license-upload`, fd); setClient((current) => ({ ...current, license_image_url: data.license_image_url, license_filename: data.license_filename, license_status: "pending" })); toast.success("License replaced and moved to pending"); } catch (err) { toast.error(err.response?.data?.detail || "License replacement failed"); } };

  // Admin one-click verify / reject actions. When verifying, prompt for the
  // license expiry date so HQ can track annual re-verification.
  const reviewLicense = async (status) => {
    let expires_at;
    if (status === "verified") {
      const current = client.license_expires_at ? String(client.license_expires_at).slice(0, 10) : "";
      const answer = window.prompt(
        "Set license expiry date (YYYY-MM-DD) so we know when re-verification is due:",
        current
      );
      if (answer === null) return; // cancelled
      const trimmed = (answer || "").trim();
      if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        toast.error("Enter a valid date in YYYY-MM-DD format");
        return;
      }
      expires_at = trimmed || "";
    }
    try {
      const body = { status };
      if (expires_at !== undefined) body.expires_at = expires_at;
      await api.patch(`/clients/${id}/license`, body);
      toast.success(`License ${status}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, {
        ...form,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        balance: Number(form.balance) || 0,
        birthday: form.birthday || null, anniversary: form.anniversary || null,
      });
      toast.success("Client updated");
      setEditOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="client-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/clients" className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="back-to-clients-link">
          <ArrowLeft size={14} /> Back to clients
        </Link>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openEdit} data-testid="detail-edit-button"><Pencil size={14} className="mr-1" /> Edit</Button>
            <Button variant="outline" onClick={remove} className="text-red-600 hover:text-red-700" data-testid="detail-delete-button"><Trash2 size={14} className="mr-1" /> Delete</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className={`p-6 lg:col-span-1 ${isVip ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white" : ""}`} data-testid="client-profile-card">
          <div className="flex items-start gap-4">
            <div className={`w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ${isVip ? "ring-4 ring-amber-400" : "bg-gray-200"}`}>
              {client.photo_url ? <img src={client.photo_url} alt={client.name} className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center text-3xl text-gray-500 bg-gray-200">{client.name?.[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                {isVip && <Crown size={16} className="text-amber-500" data-testid="vip-detail-badge" />}
                <h2 className="text-2xl font-bold tracking-tight capitalize truncate" data-testid="detail-name">{client.name}</h2>
              </div>
              {client.position_title && <p className="mt-0.5 text-sm font-medium text-[var(--brand)]" data-testid="client-position-title">{client.position_title}</p>}
              <p className="text-gray-500 capitalize truncate">{client.company || "—"}</p>
              <div className="mt-2"><StatusPill status={client.status} testid="client-status-pill" /></div>
            </div>
          </div>
            {isAdmin && <div className="mt-3 flex flex-wrap gap-2"><a href={client.photo_url} download className="inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium" data-testid="client-download-photo-button"><Download size={13} className="mr-1" /> Download</a>{client.photo_url && <Button size="sm" variant="outline" disabled={enhancingPhoto} onClick={enhancePhoto} data-testid="client-enhance-photo-button"><Sparkles size={13} className="mr-1" />{enhancingPhoto ? "Enhancing…" : "Enhance"}</Button>}<label className="inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium cursor-pointer"><Upload size={13} className="mr-1" /> Replace<input type="file" accept="image/*" className="hidden" onChange={(e) => replacePhoto(e.target.files?.[0])} data-testid="client-replace-photo-input" /></label></div>}

          <dl className="mt-6 space-y-3 text-sm">
            <div><dt className="label-eyebrow">Email</dt><dd className="break-all" data-testid="detail-email">{client.email || "—"}</dd></div>
            <div><dt className="label-eyebrow">Phone</dt><dd data-testid="detail-phone">{client.phone || "—"}</dd></div>
            <div><dt className="label-eyebrow">Address</dt>
              <dd data-testid="detail-address">
                {client.address || "—"}
                {(client.city || client.state || client.zip_code) &&
                  <div className="text-gray-500 text-xs mt-0.5">
                    <MapPin size={10} className="inline mr-1" />
                    <span className="capitalize">{client.city}</span>{client.state ? `, ${client.state}` : ""} {client.zip_code}
                  </div>}
              </dd>
            </div>
            <div><dt className="label-eyebrow">Outstanding balance</dt>
              <dd className={`tabular-nums text-lg font-semibold ${(client.balance || 0) > 0 ? "text-red-600" : "text-gray-700"}`} data-testid="detail-balance">
                {(client.balance || 0) > 0 ? `-$${(client.balance || 0).toFixed(2)}` : `$${(client.balance || 0).toFixed(2)}`}
              </dd>
            </div>
            <div>
              <dt className="label-eyebrow">Resale sub-portal</dt>
              <dd className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate flex-1" data-testid="resale-url">{portalUrl}</code>
                <Button size="sm" variant="outline" onClick={copyUrl} data-testid="copy-resale-url"><Copy size={12} /></Button>
              </dd>
              <div className="mt-3 flex items-center gap-3 rounded-lg border border-[var(--border)] bg-white p-3" data-testid="substore-qr-block">
                <div className="rounded-md border border-[var(--border)] p-1.5 bg-white shrink-0">
                  <QRCodeSVG id="client-substore-qr" value={portalUrl} size={92} level="M" bgColor="#ffffff" fgColor="#B01E24" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[var(--ink)]">Client sub-store QR</p>
                  <p className="text-[11px] text-gray-500 leading-snug mb-2">Share with the client to hand out to their customers. Admins &amp; HQ only.</p>
                  <Button size="sm" variant="outline" onClick={downloadQr} data-testid="download-substore-qr" className="h-7 text-xs">
                    <Download size={12} className="mr-1" /> Download PNG
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2" data-testid="mpire-bucks-summary">
                <div className="rounded-lg border border-[var(--border)] bg-white p-3">
                  <p className="label-eyebrow text-[var(--brand)]">MPire Buck$</p>
                  <p className="text-xl font-bold tabular-nums mt-1" data-testid="mpire-bucks-value">${Number(client.mpire_bucks_balance || 0).toFixed(2)}</p>
                </div>
                <div className="rounded-lg border border-[var(--border)] bg-white p-3">
                  <p className="label-eyebrow">Wholesale Discount</p>
                  <p className="text-xl font-bold tabular-nums mt-1" data-testid="wholesale-split-value">
                    {client.wholesale_split_percent != null ? `${Number(client.wholesale_split_percent).toFixed(1)}%` : <span className="text-sm font-medium text-gray-500">default</span>}
                  </p>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-[var(--border)] bg-white p-3" data-testid="license-card">
                <div className="flex items-center justify-between">
                  <p className="label-eyebrow">Cosmetology license</p>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      client.license_status === "verified" ? "bg-emerald-100 text-emerald-800" :
                      client.license_status === "pending"  ? "bg-amber-100 text-amber-800" :
                      client.license_status === "rejected" ? "bg-red-100 text-red-800" :
                                                             "bg-red-100 text-red-800"
                    }`}
                    data-testid="detail-license-status"
                  >
                    {client.license_status || "missing"}
                  </span>
                </div>
                <p className="mt-2 text-sm font-mono tabular-nums text-[var(--ink)]" data-testid="detail-license-number">
                  {client.license_number || <span className="text-gray-400 italic">Not provided</span>}
                </p>
                {client.license_expires_at && (() => {
                  const exp = String(client.license_expires_at).slice(0, 10);
                  const daysLeft = Math.round((new Date(exp).getTime() - Date.now()) / 86400000);
                  const tone = daysLeft < 0 ? "text-red-700" : daysLeft <= 30 ? "text-amber-700" : "text-gray-500";
                  const label = daysLeft < 0 ? `Expired ${-daysLeft}d ago` : daysLeft === 0 ? "Expires today" : `Expires in ${daysLeft}d`;
                  return (
                    <p className={`mt-1 text-[11px] font-semibold uppercase tracking-widest ${tone}`} data-testid="detail-license-expiry">
                      Expires {exp} · {label}
                    </p>
                  );
                })()}
                {client.license_image_url ? (
                  <a
                    href={client.license_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block"
                    data-testid="detail-license-thumb-link"
                  >
                    <img
                      src={client.license_image_url}
                      alt="Cosmetology license"
                      className="h-24 w-32 rounded border border-[var(--border)] object-cover hover:opacity-90 transition-opacity"
                      data-testid="detail-license-thumb"
                    />
                  </a>
                ) : (
                  <p className="mt-2 text-xs text-gray-400 italic">No license image on file</p>
                )}
                {isAdmin && client.license_image_url && <div className="mt-2 flex flex-wrap gap-2"><a href={client.license_image_url} download={client.license_filename || "license"} className="inline-flex h-7 items-center rounded-md border px-2 text-xs font-medium" data-testid="license-download-button"><Download size={12} className="mr-1" /> Download</a><Button size="sm" variant="outline" onClick={enhanceLicense} data-testid="license-enhance-button"><Sparkles size={12} className="mr-1" /> Enhance</Button><label className="inline-flex h-7 items-center rounded-md border px-2 text-xs font-medium cursor-pointer"><Upload size={12} className="mr-1" /> Replace<input type="file" accept="image/*" className="hidden" onChange={(e) => replaceLicense(e.target.files?.[0])} data-testid="license-replace-input" /></label></div>}
                {isAdmin && client.license_status !== "verified" && client.license_image_url && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => reviewLicense("verified")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                      data-testid="license-verify"
                    >
                      Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reviewLicense("rejected")}
                      className="text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
                      data-testid="license-reject"
                    >
                      Reject
                    </Button>
                  </div>
                )}
                {isAdmin && client.license_status === "verified" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reviewLicense("pending")}
                    className="mt-3 h-7 text-xs"
                    data-testid="license-unverify"
                  >
                    Move back to pending
                  </Button>
                )}
              </div>
            </div>
            {isAdmin && (
              <Link to={`/retail/${client.id}/manage`} className="inline-block mt-3 text-sm text-[var(--brand)] hover:underline" data-testid="manage-retail-link">
                Manage retail prices →
              </Link>
            )}
          </dl>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {/* Square insights */}
          {(client.lifetime_spend || client.transaction_count || client.first_visit || client.square_customer_id) && (
            <Card className="p-6" data-testid="square-insights-card">
              <p className="label-eyebrow mb-3 flex items-center gap-1">
                <DollarSign size={12} className="text-emerald-600" /> Purchase history
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Lifetime spend</div>
                  <div className={`text-2xl font-bold tabular-nums text-emerald-600`} data-testid="lifetime-spend">
                    ${(client.lifetime_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Transactions</div>
                  <div className="text-2xl font-bold tabular-nums flex items-center gap-1" data-testid="tx-count">
                    <ShoppingBag size={16} className="text-gray-400" />{client.transaction_count || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Avg / txn</div>
                  <div className="text-2xl font-bold tabular-nums" data-testid="avg-tx">
                    ${client.transaction_count ? ((client.lifetime_spend || 0) / client.transaction_count).toFixed(2) : "0.00"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Source</div>
                  <div className="text-sm font-semibold pt-2 capitalize">{client.creation_source || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-[var(--border)] text-xs">
                <div><span className="text-gray-500">First visit:</span> <span className="font-semibold" data-testid="first-visit">{client.first_visit || "—"}</span></div>
                <div><span className="text-gray-500">Last visit:</span> <span className="font-semibold" data-testid="last-visit">{client.last_visit || "—"}</span></div>
                {client.email_subscription_status && <div><span className="text-gray-500">Email opt-in:</span> <span className="font-semibold">{client.email_subscription_status}</span></div>}
                {client.nickname && <div><span className="text-gray-500">Nickname:</span> <span className="font-semibold capitalize">{client.nickname}</span></div>}
                {client.square_customer_id && <div className="col-span-2 md:col-span-3"><span className="text-gray-500">Square ID:</span> <code className="text-[10px] bg-gray-100 px-1 rounded">{client.square_customer_id}</code></div>}
              </div>
            </Card>
          )}

          <Card className="p-6" data-testid="onboarding-checklist">
            <p className="label-eyebrow mb-3">Onboarding checklist</p>
            <ul className="space-y-3">
              {STEPS.map(({ key, label }) => (
                <li key={key} className="flex items-center gap-3">
                  <Checkbox
                    checked={!!client.onboarding?.[key]}
                    onCheckedChange={(v) => toggleStep(key, !!v)}
                    disabled={!isAdmin}
                    data-testid={`onboarding-${key}`} />
                  <span className={`${client.onboarding?.[key] ? "line-through text-gray-400" : ""}`}>{label}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-6" data-testid="important-dates">
            <p className="label-eyebrow mb-3">Important dates</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2"><Cake size={16} className="text-pink-500" /> Birthday: <span className="font-semibold">{client.birthday || "—"}</span></div>
              <div className="flex items-center gap-2"><Gift size={16} className="text-purple-500" /> Anniversary: <span className="font-semibold">{client.anniversary || "—"}</span></div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Auto-reminders fire 7 days before each event.</p>
          </Card>

          {client.has_multiple_locations && (client.additional_locations || []).length > 0 && (
            <Card className="p-6" data-testid="additional-locations-card">
              <p className="label-eyebrow mb-3 flex items-center gap-1"><MapPin size={12} className="text-[var(--brand)]" /> Additional locations</p>
              <ul className="space-y-3">
                {client.additional_locations.map((loc, i) => (
                  <li key={i} className="border border-[var(--border)] rounded p-3 bg-white" data-testid={`view-location-${i}`}>
                    <div className="font-semibold text-sm capitalize">{loc.label || `Address #${i + 2}`}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {loc.address || "—"}
                      {(loc.city || loc.state || loc.zip_code) && (
                        <div className="text-xs text-gray-500">
                          <span className="capitalize">{loc.city}</span>{loc.state ? `, ${loc.state}` : ""} {loc.zip_code}
                        </div>
                      )}
                      {loc.phone && <div className="text-xs text-gray-500">📞 {loc.phone}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {client.notes && (
            <Card className="p-6" data-testid="client-notes-card">
              <p className="label-eyebrow mb-2">Notes / Memo</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
            </Card>
          )}

          <Card className="p-6" data-testid="order-history">
            <p className="label-eyebrow mb-3">Order history (in MPower!)</p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="py-2">Date</th><th>Status</th><th>Fulfilled by</th><th className="text-right">Total</th></tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-t border-[var(--border)]">
                    <td className="py-2">{o.created_at?.slice(0, 10)}</td>
                    <td><StatusPill status={o.status} /></td>
                    <td className="capitalize">{o.fulfilled_by_name || "—"}</td>
                    <td className="text-right tabular-nums">${o.total.toFixed(2)}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-400">No MPower! orders yet.{client.transaction_count > 0 && <span className="block mt-1 text-xs">({client.transaction_count} historical Square transactions summarized above)</span>}</td></tr>}
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {/* Edit sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="edit-client-sheet">
          <SheetHeader><SheetTitle>Edit {client.name}</SheetTitle></SheetHeader>
          {form && (
            <form onSubmit={submitEdit} className="space-y-4 mt-6" data-testid="edit-client-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></div>
                <div className="col-span-2"><Label>Position Title</Label><select value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} className="mt-1 flex h-11 w-full rounded-md border border-input bg-background px-3 text-base" data-testid="edit-position-title"><option value="">Select title</option>{["Hair/Salon Stylist","Braider/Loctition","Master Groomer/Barber","Owner","Manager","Other"].map((title)=><option key={title}>{title}</option>)}</select>{form.position_title === "Other" && <Input className="mt-2" value={form.other_position_title} onChange={(e)=>setForm({...form,other_position_title:e.target.value})} placeholder="Enter a title" data-testid="edit-other-position-title" />}</div>
                <div className="col-span-2"><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="edit-company" /></div>
                <div className="col-span-2">
                  <Label>Distributing Partner *</Label>
                  <Select value={form.endpoint_partner_id || undefined} onValueChange={(v) => setForm({ ...form, endpoint_partner_id: v })}>
                    <SelectTrigger data-testid="edit-partner"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.company ? ` (${p.company})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-address" /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city" /></div>
                <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="edit-state" /></div>
                <div><Label>Zip code</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="edit-zip" /></div>
                <div><Label>Balance</Label><Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="edit-balance" /></div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    Wholesale split % <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--brand)]/10 text-[var(--brand)]">HQ only</span>
                  </Label>
                  <Input type="number" min="0" max="100" step="0.5" value={form.wholesale_split_percent ?? ""} onChange={(e) => setForm({ ...form, wholesale_split_percent: e.target.value === "" ? null : Number(e.target.value) })} placeholder="Uses global default" data-testid="edit-wholesale-split" />
                  <p className="text-[10px] text-gray-500 mt-1">Overrides the global default. Hidden from the client.</p>
                </div>
                <div><Label>Latitude</Label><Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="edit-lat" /></div>
                <div><Label>Longitude</Label><Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="edit-lng" /></div>
                <div className="col-span-2"><Label>Photo URL</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="edit-photo" /></div>
                <div><Label>Birthday</Label><Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="edit-birthday" /></div>
                <div><Label>Anniversary</Label><Input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} data-testid="edit-anniversary" /></div>
                <div className="col-span-2"><Label>Notes / Memo</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" /></div>
                <LocationsSection value={form} onChange={(patch) => setForm({ ...form, ...patch })} />
              </div>
              <Button className="btn-brand w-full" disabled={saving} data-testid="edit-save-button">{saving ? "Saving…" : "Save changes"}</Button>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
